/**
 * Centralized project role and permission checks.
 *
 * Use the `can...` helpers in UI instead of scattering role comparisons across components.
 *
 * Role strings (e.g. `OWNER`, `COLLABORATOR`) come from `members[].role`.
 * Permission flags (e.g. `isOwner`, `isCollaborator`) are derived booleans for what the user can do.
 */
import { useMemo } from 'react';
import { useAuth } from '@/common/auth/hooks/useAuth';
import { ProjectMemberRole } from '@/common/models/enums';
import type { ProjectMemberRole as GraphQLMemberRole } from '@/generated/graphql';

/** Minimal project shape for permission checks. Accepts full GraphQL `ProjectType`. */
export type ProjectWithMembers = {
  members?: Array<{ userId: string; role: GraphQLMemberRole }> | null;
};

/** Permission flags for the current user — not the same as role strings on members[]. */
export type ProjectRoleFlags = {
  isOwner: boolean;
  isCollaborator: boolean;
  isReadOnly: boolean;
};

/** Returns the current user's row in `project.members`, or undefined if not listed. */
function getMember(
  project: ProjectWithMembers | null | undefined,
  userId: string | null | undefined
) {
  if (!project || !userId) return undefined;
  return project.members?.find((m) => m.userId === userId);
}

/** Derives permission flags from the current user's `members[].role`. */
export function getProjectRoleFlags(
  project: ProjectWithMembers | null | undefined,
  userId: string | null | undefined
): ProjectRoleFlags {
  const memberRole = getMember(project, userId)?.role;

  return {
    isOwner: memberRole === ProjectMemberRole.OWNER,
    isCollaborator: memberRole === ProjectMemberRole.COLLABORATOR,
    isReadOnly: memberRole === ProjectMemberRole.READ_ONLY,
  };
}

export function canDelete(roles: ProjectRoleFlags): boolean {
  return roles.isOwner;
}

export function canEdit(roles: ProjectRoleFlags): boolean {
  return roles.isOwner;
}

/** Can open the invite dialog. */
export function canInvite(roles: ProjectRoleFlags): boolean {
  return roles.isOwner;
}

/** Roles the current user may assign when sending an invitation. */
export function getInvitableRoles(
  roles: ProjectRoleFlags
): ProjectMemberRole[] {
  if (!canInvite(roles)) return [];

  return [ProjectMemberRole.COLLABORATOR, ProjectMemberRole.READ_ONLY];
}

export function canInviteRole(
  roles: ProjectRoleFlags,
  inviteRole: GraphQLMemberRole
): boolean {
  if (inviteRole === ProjectMemberRole.OWNER) return false;
  if (
    inviteRole === ProjectMemberRole.COLLABORATOR ||
    inviteRole === ProjectMemberRole.READ_ONLY
  ) {
    return canInvite(roles);
  }
  return false;
}

export function canViewInvites(roles: ProjectRoleFlags): boolean {
  return roles.isOwner;
}

export function canCancelInvite(
  roles: ProjectRoleFlags,
  inviteRole: GraphQLMemberRole
): boolean {
  return (
    roles.isOwner &&
    (inviteRole === ProjectMemberRole.COLLABORATOR ||
      inviteRole === ProjectMemberRole.READ_ONLY)
  );
}

export function canRemoveMember(
  roles: ProjectRoleFlags,
  targetRole: GraphQLMemberRole
): boolean {
  if (targetRole === ProjectMemberRole.OWNER) return false;
  return (
    roles.isOwner &&
    (targetRole === ProjectMemberRole.COLLABORATOR ||
      targetRole === ProjectMemberRole.READ_ONLY)
  );
}

export function canUpload(roles: ProjectRoleFlags): boolean {
  return roles.isOwner || roles.isCollaborator;
}

/** Can add comments and replies. */
export function canComment(roles: ProjectRoleFlags): boolean {
  return roles.isOwner || roles.isCollaborator || roles.isReadOnly;
}

/** Can resolve and reopen comment threads. */
export function canResolveComments(roles: ProjectRoleFlags): boolean {
  return roles.isOwner || roles.isCollaborator;
}

export function canLeave(roles: ProjectRoleFlags): boolean {
  return !roles.isOwner && (roles.isCollaborator || roles.isReadOnly);
}

/** Resolved permissions for the current user on a project (hook return type). */
export type ProjectPermissions = ProjectRoleFlags & {
  canDelete: boolean;
  canEdit: boolean;
  canUpload: boolean;
  canInvite: boolean;
  canLeave: boolean;
  canComment: boolean;
  canResolveComments: boolean;
  canViewInvites: boolean;
  invitableRoles: ProjectMemberRole[];
  canRemoveMember: (targetRole: GraphQLMemberRole) => boolean;
  canCancelInvite: (inviteRole: GraphQLMemberRole) => boolean;
};

export function buildProjectPermissions(
  roles: ProjectRoleFlags
): ProjectPermissions {
  return {
    ...roles,
    canDelete: canDelete(roles),
    canEdit: canEdit(roles),
    canUpload: canUpload(roles),
    canInvite: canInvite(roles),
    canLeave: canLeave(roles),
    canComment: canComment(roles),
    canResolveComments: canResolveComments(roles),
    canViewInvites: canViewInvites(roles),
    invitableRoles: getInvitableRoles(roles),
    canRemoveMember: (targetRole) => canRemoveMember(roles, targetRole),
    canCancelInvite: (inviteRole) => canCancelInvite(roles, inviteRole),
  };
}

/** React hook — permissions for the authenticated user on the given project. */
export function useProjectPermissions(
  project: ProjectWithMembers | null | undefined
): ProjectPermissions {
  const { userId } = useAuth();
  return useMemo(() => {
    const roles = getProjectRoleFlags(project, userId);
    return buildProjectPermissions(roles);
  }, [project, userId]);
}
